import { parseISO } from 'date-fns';
import type {
  ProgramContentObjectDataObject,
  ProgramContentObjectItemDataObject,
  ProgramContentObjectItemDetailDataObject,
  PublicProgramContentObjectDataObject,
} from 'models/service';
import { type Image, createImage } from '../../image';
import { type OrganizationContentObject, createOrganizationContentObject } from './organization-content-object';

export type ProgramContentObject = {
  contentableType: 'Program';
  evaluation?: number;
  evaluationRatingAverage?: string;
  program: ProgramContentObjectItem;
  programsTopic: ProgramTopic;
} & OrganizationContentObject;

export type ProgramTopic = { id: string; name: string };

export const createProgramContentObject = (data: ProgramContentObjectDataObject): ProgramContentObject => ({
  ...createOrganizationContentObject(data),
  contentableType: data.contentable_type,
  evaluation: data.evaluation,
  evaluationRatingAverage: data.evaluation_rating_average,
  program: createProgramContentObjectData(data.program),
  programsTopic: data.programs_topic,
});

export type ProgramContentObjectItem = {
  id: string;
  name: string;
  profilePhoto?: Image;
  settings?: { allowComments: boolean; allowUnsubscribe: boolean; viewMembers: boolean };
  status: 'draft' | 'published';
};

export const createProgramContentObjectData = (data: ProgramContentObjectItemDataObject): ProgramContentObjectItem => ({
  id: data.id,
  name: data.name,
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  settings: data.settings
    ? {
        allowComments: data.settings.allow_comments,
        allowUnsubscribe: data.settings.allow_unsubscribe,
        viewMembers: data.settings.view_members,
      }
    : undefined,
  status: data.status,
});

export type ProgramContentObjectItemDetail = {
  administrators: Array<{ id: string }>;
  author: { id: string };
  coverPhoto?: Image;
  createdAt: Date;
  endTime: Date;
  featuredInChannels: boolean;
  joined: boolean;
  membersCount: number;
  price: null | string;
  priceOption: ProgramContentObjectItemDetailDataObject['price_option'];
  programType: ProgramContentObjectItemDetailDataObject['program_type'];
  sponsors: ProgramContentObjectItemDetailDataObject['sponsors'];
  startTime: Date;
} & ProgramContentObjectItem;

export const createProgramContentObjectDetailData = (
  data: ProgramContentObjectItemDetailDataObject,
): ProgramContentObjectItemDetail => ({
  ...createProgramContentObjectData(data),
  administrators: data.administrators,
  author: data.author,
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  createdAt: parseISO(data.created_at),
  endTime: parseISO(data.end_time),
  featuredInChannels: data.featured_in_channels,
  joined: data.joined,
  membersCount: data.members_count,
  price: data.price,
  priceOption: data.price_option,
  programType: data.program_type,
  sponsors: data.sponsors,
  startTime: parseISO(data.start_time),
});

export type ProgramContentObjectDetail = {
  program: ProgramContentObjectItemDetail;
} & Omit<ProgramContentObject, 'program'>;

export type ProgramContentObjectList = {
  program: ProgramContentObjectItem;
} & ProgramContentObject;

export const createProgramContentObjectDetail = (
  data: { program: ProgramContentObjectItemDetailDataObject } & ProgramContentObjectDataObject,
): ProgramContentObjectDetail => ({
  ...createProgramContentObject(data),
  program: createProgramContentObjectDetailData(data.program),
});

export const createProgramContentObjectList = (data: ProgramContentObjectDataObject): ProgramContentObjectList =>
  createProgramContentObject(data);

// Public
export type PublicProgramContentObject = { contentableType: 'Program' };

export const createPublicProgramContentObject = (
  data: PublicProgramContentObjectDataObject,
): PublicProgramContentObject => ({
  contentableType: data.contentable_type,
});
